import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useIsSuperAdmin } from "../../access/AccessProvider";
import {
  grantAdminUserRoleMutationOptions,
  operatorsAdminEffectiveQueryOptions,
  operatorsAdminUsersQueryOptions,
  revokeAdminUserRoleMutationOptions,
  updateAdminUserMutationOptions,
} from "../../lib/operatorsAdminReactQuery";
import type { OperatorsAdminUser } from "../../operators";

import { AdminEffectivePermissionsDrawer } from "./AdminEffectivePermissionsDrawer";
import { AdminUserActionsMenu } from "./AdminUserActionsMenu";
import { AdminUserRolesCombobox } from "./AdminUserRolesCombobox";
import { SettingsInfoTip } from "./SettingsInfoTip";
import { SentryInvestigationsSection } from "./SentryInvestigationsSection";
import { SlackPrivateChannelsSection } from "./SlackPrivateChannelsSection";
import { SettingsPageHeader, SettingsPanel, SettingsSection } from "./SettingsChrome";

export function AdminSettingsPanel() {
  const isSuperAdmin = useIsSuperAdmin();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const usersQuery = useQuery(operatorsAdminUsersQueryOptions({ enabled: isSuperAdmin, query }));
  const effectiveQuery = useQuery(
    operatorsAdminEffectiveQueryOptions({ enabled: isSuperAdmin, userId: selectedUserId }),
  );

  const grantRole = useMutation(grantAdminUserRoleMutationOptions({ queryClient }));
  const revokeRole = useMutation(revokeAdminUserRoleMutationOptions({ queryClient }));
  const updateUser = useMutation(updateAdminUserMutationOptions({ queryClient }));

  const selectedUser = useMemo(
    () => usersQuery.data?.items.find((user) => user.id === selectedUserId) ?? null,
    [selectedUserId, usersQuery.data?.items],
  );

  if (!isSuperAdmin) {
    return (
      <SettingsPanel>
        <SettingsPageHeader
          title="Admin"
          description="Super admin access is required to manage roles and governance."
        />
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel>
      <SettingsPageHeader
        title="Admin"
        description="Grant and revoke roles and inspect effective permissions."
      />

      <SettingsSection
        flush
        title="Users"
        description="People who can sign in to Operators. Click a row to inspect their effective permissions."
        actions={
          <SettingsInfoTip label="Everyone listed here has signed in at least once. Role and privilege changes apply on their next API request (usually within 30 seconds)." />
        }
      >
        <div className="border-b border-border/70 px-5 py-3">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter by email or name"
            className="h-9 w-full max-w-xs rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Filter users"
          />
        </div>

        {usersQuery.isLoading ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">Loading users…</p>
        ) : usersQuery.isError ? (
          <p className="px-5 py-6 text-sm text-destructive">
            {usersQuery.error instanceof Error ? usersQuery.error.message : "Failed to load users"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-3 text-left">
                    <span className="inline-flex items-center gap-1.5">
                      User
                      <SettingsInfoTip label="Display name and email from Google sign-in. Super admin is a special privilege, not a normal role." />
                    </span>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <span className="inline-flex items-center gap-1.5">
                      Roles
                      <SettingsInfoTip label="Roles control which tools and APIs someone can use. Member is the baseline for every employee and cannot be removed." />
                    </span>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <span className="inline-flex items-center gap-1.5">
                      Status
                      <SettingsInfoTip label="Suspended users cannot sign in or use Operators until they are activated again." />
                    </span>
                  </th>
                  <th className="px-5 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(usersQuery.data?.items ?? []).map((user) => (
                  <AdminUserRow
                    key={user.id}
                    user={user}
                    roleKeys={usersQuery.data?.roleKeys ?? []}
                    selected={selectedUserId === user.id}
                    busy={grantRole.isPending || revokeRole.isPending || updateUser.isPending}
                    onSelect={() => setSelectedUserId(user.id)}
                    onGrant={async (roleKey) => {
                      await grantRole.mutateAsync({ roleKey, userId: user.id });
                    }}
                    onRevoke={async (roleKey) => {
                      await revokeRole.mutateAsync({ roleKey, userId: user.id });
                    }}
                    onToggleSuperAdmin={async (next) => {
                      await updateUser.mutateAsync({ isSuperAdmin: next, userId: user.id });
                    }}
                    onToggleStatus={async (status) => {
                      await updateUser.mutateAsync({ status, userId: user.id });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsSection>

      <SlackPrivateChannelsSection />
      <SentryInvestigationsSection />

      <AdminEffectivePermissionsDrawer
        open={selectedUserId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedUserId(null);
          }
        }}
        effectiveQuery={effectiveQuery}
        selectedEmail={selectedUser?.email ?? null}
        selectedName={selectedUser?.displayName ?? null}
      />
    </SettingsPanel>
  );
}

function AdminUserRow(props: {
  busy: boolean;
  onGrant: (roleKey: string) => Promise<void>;
  onRevoke: (roleKey: string) => Promise<void>;
  onSelect: () => void;
  onToggleStatus: (status: "active" | "suspended") => Promise<void>;
  onToggleSuperAdmin: (next: boolean) => Promise<void>;
  roleKeys: string[];
  selected: boolean;
  user: OperatorsAdminUser;
}) {
  return (
    <tr
      className={`border-b border-border/70 ${props.selected ? "bg-primary/5" : "hover:bg-muted/30"}`}
      onClick={props.onSelect}
    >
      <td className="px-5 py-3 align-middle text-left">
        <p className="font-medium text-foreground">{props.user.displayName}</p>
        <p className="text-xs text-muted-foreground">{props.user.email}</p>
        {props.user.isSuperAdmin ? (
          <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-primary">
            Super admin
            <SettingsInfoTip label="Super admins bypass normal permission checks. This is a privilege on the user record, not a grantable role." />
          </p>
        ) : null}
      </td>
      <td className="px-5 py-3 align-middle text-left">
        <AdminUserRolesCombobox
          busy={props.busy}
          roleKeys={props.roleKeys}
          roles={props.user.roles}
          onGrant={props.onGrant}
          onRevoke={props.onRevoke}
        />
      </td>
      <td className="px-5 py-3 align-middle text-left">
        <span className={props.user.status === "active" ? "text-foreground" : "text-destructive"}>
          {props.user.status}
        </span>
      </td>
      <td className="px-5 py-3 align-middle text-left">
        <AdminUserActionsMenu
          busy={props.busy}
          isSuperAdmin={props.user.isSuperAdmin}
          status={props.user.status}
          onToggleSuperAdmin={props.onToggleSuperAdmin}
          onToggleStatus={props.onToggleStatus}
        />
      </td>
    </tr>
  );
}
